'use es6';

import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import throttle from 'transmute/throttle';
import debounce from 'transmute/debounce';
import emptyFunction from 'react-utils/emptyFunction';
import styled from 'styled-components';
export const ON_SCROLL_THROTTLE = 100;
export const DEBOUNCE_TIMEOUT = 300;

// TODO: figure out where this is coming from
const MESSAGE_SCROLL_OFFSET = -120;
import { getDOMNodeByMessageId } from '../operators/getDOMNodeByMessageId';

// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/contentBoxSize#value
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getHeightFromResizeObserverEntries = entries => {
  try {
    return entries[0].contentBoxSize[0].blockSize || 0;
  } catch (e) {
    return 0;
  }
};
const OverflowContainer = styled.div.withConfig({
  displayName: "ScrollWrapper__OverflowContainer",
  componentId: "mryban-0"
})(["overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;align-self:stretch;flex-grow:1;"]);
class ScrollWrapper extends PureComponent {
  constructor(props) {
    super(props);
    this.setBottomLock = () => {
      // Handle instances where we're close enough to the bottom to lock
      if (this.state.distanceToBottom < 5) {
        // Lock scroll to the bottom if a user has scrolled to the bottom
        this.setState({
          shouldKeepAtBottom: true
        });
      }
    };
    this.onFinishedResizing = () => {
      this.setState({
        hasFinishedResizing: true
      });
      if (this.props.scrollToMessageId && !this.foundDeeplinkedMessage.current) {
        this.scrollToMessage(this.props.scrollToMessageId);
      }
      if (!this.state.hasFinishedMountScroll && !this.props.scrollToMessageId) {
        this.scrollToBottom();
        this.setState({
          hasFinishedMountScroll: true
        });
      }
    };
    this.state = {
      inTop: true,
      inBottom: true,
      height: 0,
      distanceToBottom: 0,
      shouldKeepAtBottom: !this.props.scrollToMessageId,
      previousContentHeight: 0,
      hasFinishedResizing: false,
      hasFinishedMountScroll: false
    };
    this.scrollContainer = /*#__PURE__*/createRef();
    this.scrollContent = /*#__PURE__*/createRef();
    this.foundDeeplinkedMessage = /*#__PURE__*/createRef();
    const contentResizeCallback = entries => {
      const newContentHeight = getHeightFromResizeObserverEntries(entries);
      const contentGrew = this.state.previousContentHeight < newContentHeight;
      if (contentGrew && this.inThreshold().inTop) {
        this.props.onScrollToTop();
      }
      this.setState({
        previousContentHeight: newContentHeight
      });
      this.onFinishedResizing();
    };
    contentResizeCallback.bind(this);
    this.contentResizeObserver = new ResizeObserver(contentResizeCallback);
    const containerResizeCallback = () => {
      if (this.state.shouldKeepAtBottom) {
        this.scrollToBottom();
      }
    };
    containerResizeCallback.bind(this);
    this.containerResizeObserver = new ResizeObserver(containerResizeCallback);
    this.onResize = throttle(ON_SCROLL_THROTTLE, this.onResize.bind(this));
    this.onScroll = throttle(ON_SCROLL_THROTTLE, this.onScroll.bind(this));
    this.setBottomLock = debounce(DEBOUNCE_TIMEOUT, this.setBottomLock);
    this.onFinishedResizing = debounce(DEBOUNCE_TIMEOUT, this.onFinishedResizing);
  }
  componentDidMount() {
    const {
      current: currentScrollContent
    } = this.scrollContent;
    const {
      current: currentScrollContainer
    } = this.scrollContainer;
    if (currentScrollContent) {
      this.setState({
        height: currentScrollContent.clientHeight
      });
      if (!this.props.scrollToMessageId) {
        this.scrollToBottom();
      }
      this.contentResizeObserver.observe(currentScrollContent);
    }
    if (currentScrollContainer) {
      this.containerResizeObserver.observe(currentScrollContainer);
    }
    window.addEventListener('resize', this.onResize, {
      passive: true
    });
  }
  getSnapshotBeforeUpdate() {
    const {
      current
    } = this.scrollContainer;
    if (!current) return null;
    return {
      prevScrollTop: this.getScrollDistanceFromTop()
    };
  }
  componentDidUpdate(__prevProps, prevState, snapshot) {
    const {
      current
    } = this.scrollContainer;
    if (!snapshot || !current) return;

    //if the deeplinked message changed, scroll to the new deeplinked message
    if (this.props.scrollToMessageId && this.props.scrollToMessageId !== __prevProps.scrollToMessageId) {
      this.foundDeeplinkedMessage.current = false;
      this.scrollToMessage(this.props.scrollToMessageId);
      return;
    }
    if (this.state.shouldKeepAtBottom) {
      this.scrollToBottom();
      return;
    }
    if (this.state.hasFinishedResizing && !this.state.shouldKeepAtBottom) {
      this.setBottomLock();
    }
    const {
      prevScrollTop
    } = snapshot;
    const currentScrollTop = this.getScrollDistanceFromTop();
    if (currentScrollTop !== prevScrollTop) return;
    if (prevState.height > this.state.height) {
      this.setScrollTop(currentScrollTop + (prevState.height - this.state.height));
    }
  }
  componentWillUnmount() {
    this.contentResizeObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  // Debounced to wait for UI to stop updating scroll position

  // Debounced so it's only called once after rapid resizing

  getScrollDistanceFromTop() {
    const {
      current: scrollWrapper
    } = this.scrollContainer;
    const {
      current: content
    } = this.scrollContent;
    const heightDiff = scrollWrapper.scrollHeight - content.clientHeight;
    return scrollWrapper.scrollTop - heightDiff;
  }
  onScroll() {
    const {
      onScrollToTop,
      onScrollFromBottom,
      onScrollToBottom
    } = this.props;
    if (!this.scrollContainer.current || !this.scrollContent.current) {
      return;
    }
    const {
      inTop,
      inBottom
    } = this.inThreshold();
    if (!this.state.inTop && inTop) {
      onScrollToTop();
    }
    if (!this.state.inBottom && inBottom) {
      onScrollToBottom();
    }
    if (this.state.inBottom && !inBottom) {
      onScrollFromBottom();
    }
    const distanceToBottom = this.getScrollDistanceFromBottom();
    this.setState({
      inTop,
      inBottom,
      distanceToBottom,
      shouldKeepAtBottom: false
    });
  }
  inThreshold() {
    const {
      topThreshold,
      bottomThreshold
    } = this.props;
    const distanceFromTop = this.getScrollDistanceFromTop();
    const distanceFromBottom = this.getScrollDistanceFromBottom();
    return {
      inTop: distanceFromTop <= topThreshold,
      inBottom: distanceFromBottom <= bottomThreshold
    };
  }
  getScrollDistanceFromBottom() {
    const {
      current
    } = this.scrollContainer;
    if (!current) {
      return 0;
    }
    const {
      scrollTop,
      scrollHeight: fullContentHeight,
      clientHeight,
      offsetHeight
    } = current;
    const containerHeight = clientHeight || offsetHeight;
    return fullContentHeight - containerHeight - scrollTop;
  }
  scrollToBottom() {
    const {
      current
    } = this.scrollContainer;
    if (current) {
      this.setScrollTop(current.scrollHeight);
      this.setState({
        shouldKeepAtBottom: true
      });
    }
  }
  scrollToMessage(messageId) {
    const element = getDOMNodeByMessageId(messageId);
    if (messageId && element) {
      this.setScrollTop(element.offsetTop + MESSAGE_SCROLL_OFFSET);
      this.foundDeeplinkedMessage.current = true;
    } else {
      this.props.onScrollToTop();
      this.onFinishedResizing();
    }
  }
  setScrollTop(scrollTop) {
    const {
      current
    } = this.scrollContainer;
    if (current) {
      current.scrollTop = scrollTop || 0;
    }
    this.setState({
      shouldKeepAtBottom: false
    });
  }
  onResize() {
    const {
      current
    } = this.scrollContent;
    if (current) {
      this.setState({
        height: current.clientHeight || 0
      });
    }
  }
  render() {
    const {
      children,
      stickToTop,
      dataTestId
    } = this.props;
    return /*#__PURE__*/_jsxs(OverflowContainer, {
      "data-test-id": dataTestId,
      onScroll: this.onScroll,
      ref: this.scrollContainer,
      children: [stickToTop, /*#__PURE__*/_jsx("div", {
        ref: this.scrollContent,
        children: children
      })]
    });
  }
}
ScrollWrapper.displayName = 'ScrollWrapper';
ScrollWrapper.defaultProps = {
  bottomThreshold: 300,
  onScrollFromBottom: emptyFunction,
  onScrollToBottom: emptyFunction,
  onScrollToTop: emptyFunction,
  topThreshold: 300
};
ScrollWrapper.propTypes = {
  bottomThreshold: PropTypes.number.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  onScrollFromBottom: PropTypes.func.isRequired,
  onScrollToBottom: PropTypes.func.isRequired,
  onScrollToTop: PropTypes.func.isRequired,
  stickToTop: PropTypes.node,
  topThreshold: PropTypes.number.isRequired,
  scrollToMessageId: PropTypes.string,
  dataTestId: PropTypes.string
};
export default ScrollWrapper;